// ============================================================
// PortalContext — /olivium/portals/:portalId/context (PHASE_2L).
//
// The project-context overview for a portal: saved repo + deployment summary,
// the current PR / build "report" (honest — saved links are real; LIVE GitHub /
// build sync is INTEGRATION_DEFERRED, never a fabricated status), the next
// recommended action, and the CLIENT-ACCESS BRIDGE (private by default; publish
// only flips client_visible + writes a receipt — there is no live client portal
// yet, CLIENT_ACCESS_DEFERRED). Presentational + SSR-safe.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import { OLIVIUM_ACCENT, type PortalContextRecord } from './portalFactory'
import { PORTAL_HONESTY, SectionCard, StateBlock, BackendNotReadyBanner, chipBtn, primaryBtn, inputStyle } from './PortalKit'

export interface PortalContextProps {
  portalName?: string
  context: PortalContextRecord | null
  loading: boolean
  error: string | null
  authRequired: boolean
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenPortal?: () => void
  onOpenRepo?: () => void
  onSaveNextAction?: (text: string) => void | Promise<unknown>
  onPublish?: (visible: boolean) => void | Promise<unknown>
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 12 }}>
      <span style={{ color: CC_INK.faint, fontFamily: CC_MONO, width: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ color: value ? CC_INK.secondary : CC_INK.faint, fontFamily: mono ? CC_MONO : 'inherit', wordBreak: 'break-all' }}>{value || '—'}</span>
    </div>
  )
}

export default function PortalContext(props: PortalContextProps) {
  const { portalName, context, loading, error, authRequired, onHome, onBackCommandCenter, onOpenPortal, onOpenRepo } = props
  const [nextAction, setNextAction] = useState('')
  const backendNotReady = !!error && /not migrated/i.test(error)
  const clientVisible = context?.client_visible === true

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Portal', onClick: onOpenPortal }}
      breadcrumb={['OLIVIUM', (portalName || 'PORTAL').toUpperCase(), 'CONTEXT']}
      title="Project Context" titleAccent={OLIVIUM_ACCENT} honestyBadge={PORTAL_HONESTY}
    >
      <div>
        {backendNotReady && <BackendNotReadyBanner message={error!} />}
        {loading && <StateBlock kind="loading" title="Loading context…" />}
        {!loading && authRequired && <StateBlock kind="auth" title="Sign in to load project context" />}
        {!loading && !authRequired && error && !backendNotReady && <StateBlock kind="error" title="Could not load context" body={error} />}

        {!loading && !authRequired && !error && (
          <div>
            <SectionCard title="Repo & Deployment" right={<button type="button" style={chipBtn} onClick={onOpenRepo}>Edit repo/deploy →</button>}>
              {!context ? (
                <StateBlock kind="empty" title="No project context saved yet" body="Add repo + deployment metadata from the Repo tab. Nothing is fabricated." />
              ) : (
                <div>
                  <Field label="Provider" value={context.repo_provider} mono />
                  <Field label="Repo" value={context.repo_owner && context.repo_name ? `${context.repo_owner}/${context.repo_name}` : context.repo_name} mono />
                  <Field label="Repo URL" value={context.repo_url} mono />
                  <Field label="Default branch" value={context.default_branch} mono />
                  <Field label="Active branch" value={context.active_branch} mono />
                  <Field label="Current PR" value={context.current_pr_url} mono />
                  <Field label="Deployment" value={context.deployment_url} mono />
                  <Field label="Vercel project" value={context.vercel_project_name} mono />
                  <Field label="Supabase ref" value={context.supabase_project_ref} mono />
                </div>
              )}
            </SectionCard>

            <SectionCard title="Current PR / Build report" accent="#b8862a">
              <p style={{ margin: 0, fontSize: 11.5, color: CC_INK.muted, lineHeight: 1.6 }}>
                Saved links above are real. <strong>Live GitHub PR/build status is INTEGRATION_DEFERRED</strong> — this surface never
                fabricates a PR state, a build result, or a deployment status. When the GitHub connector is wired, live status renders here.
              </p>
              {context?.sync_status && <p style={{ margin: '8px 0 0', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>sync_status: {context.sync_status} · github_permission: {context.github_permission_status}</p>}
            </SectionCard>

            <SectionCard title="Next recommended action">
              <p style={{ margin: '0 0 8px', fontSize: 12.5, color: CC_INK.secondary }}>{context?.next_action || <em style={{ color: CC_INK.faint }}>No next action set.</em>}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={inputStyle} value={nextAction} placeholder="Set the next action…" onChange={(e) => setNextAction(e.target.value)} />
                <button type="button" style={chipBtn} onClick={async () => { if (nextAction.trim()) { await props.onSaveNextAction?.(nextAction.trim()); setNextAction('') } }}>Save</button>
              </div>
            </SectionCard>

            {/* Client Access Bridge — private by default. */}
            <SectionCard title="Client access" accent={clientVisible ? '#059669' : '#b8862a'}>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: CC_INK.secondary, lineHeight: 1.6 }}>
                This portal's context is <strong>{clientVisible ? 'marked client-visible' : 'private (default)'}</strong>. Internal DMs,
                private notes, raw blockers, and unapproved drafts are <strong>never</strong> exposed. Publishing only flags visibility and
                writes a receipt — there is no live client portal yet (<span style={{ color: '#b8862a', fontFamily: CC_MONO }}>CLIENT_ACCESS_DEFERRED</span>).
              </p>
              {context?.published_at && <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginBottom: 8 }}>published_at: {context.published_at.slice(0, 16).replace('T', ' ')}</div>}
              {clientVisible ? (
                <button type="button" style={chipBtn} onClick={() => props.onPublish?.(false)}>Return to private (unpublish)</button>
              ) : (
                <button type="button" style={primaryBtn} onClick={() => props.onPublish?.(true)}>Mark client-visible (publish)</button>
              )}
            </SectionCard>
          </div>
        )}
      </div>
    </CommandCenterShell>
  )
}
